//#region IMPORTS
import { Combobox, Transition } from "@headlessui/react";
import { MainComboboxOption } from "./MainComboBoxOption";
import { Fragment, useState } from "react";
import { ChevronUpDownIcon, XMarkIcon } from "@heroicons/react/20/solid";
//#endregion

//#region INTERFACE
interface Props {
  onChange: (value: string) => void;
  disabled?: boolean;
  options: string[];
  value?: string;
}
//#endregion

export default function MainFieldArrayFormComboBox({
  onChange,
  disabled = false,
  options,
  value,
}: Props) {
  //#region SETUP
  // `query` is ONLY the text the user types to search. It is kept separate from
  // the selected `value`: conflating them made the selected value double as the
  // filter, which (combined with the parent stripping already-selected options)
  // emptied the list so the dropdown could no longer open until cleared.
  const [query, setQuery] = useState("");

  const filtered =
    query === ""
      ? options
      : options.filter((c) => c.toLowerCase().includes(query.toLowerCase()));
  //#endregion

  //#region LOGIC
  const onChangeHandler = (selected: string) => {
    onChange(selected);
    // Reset the search so reopening shows the full (unselected) list again.
    setQuery("");
  };
  //#endregion

  //#region RENDER
  return (
    <Combobox
      as="div"
      className="grow"
      value={value || ""}
      onChange={(selected) => onChangeHandler(selected)}
      disabled={disabled}
    >
      {({ open }) => (
        <div className="flex items-center gap-4">
          <div className="relative grow">
            <Combobox.Input
              className="block w-full rounded-md px-3 py-1.5 border border-gray-300 text-gray-900 bg-white focus:border-gray-500 focus:ring-0 focus:outline-none sm:text-sm sm:leading-6 placeholder:text-gray-400 disabled:cursor-not-allowed disabled:bg-gray-100"
              onChange={(e) => setQuery(e.target.value)}
              displayValue={() => value || ""}
            />

            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none hover:cursor-pointer disabled:cursor-not-allowed">
              <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
            </Combobox.Button>

            {filtered.length > 0 ? (
              <Transition
                show={open}
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
                afterLeave={() => setQuery("")}
              >
                <Combobox.Options className="absolute z-50 mt-1 max-h-48 w-full overflow-auto rounded-lg bg-gray-100 p-2 text-sm shadow-md">
                  {filtered.map((item, i) => (
                    <MainComboboxOption key={i} value={item} text={item} />
                  ))}
                </Combobox.Options>
              </Transition>
            ) : null}
          </div>

          {/* Clear this field. Lives INSIDE <Combobox> and goes through the same
              handler as a normal selection, so Headless' focus/open state stays
              intact and the dropdown can be reopened afterwards. */}
          <button
            type="button"
            title="Clear"
            aria-label="Clear"
            disabled={disabled}
            onClick={() => onChangeHandler("")}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-200 text-gray-600 hover:bg-gray-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-400 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <XMarkIcon aria-hidden="true" className="h-4 w-4" />
          </button>
        </div>
      )}
    </Combobox>
  );
  //#endregion
}
